/**
 * Shared help text for asset sharing preferences
 * Used across SharingPreferences settings and CustomTaskForm
 */
export const ASSET_SHARING_HELP_TEXT = {
  shareAttributes:
    'Display asset attributes (e.g., serial number, model, specifications)',
  shareJobHistory: 'Display past jobs completed on the asset',
  shareDeploymentHistory:
    'Display where the asset has been deployed previously',
} as const;

export type AssetSharingSettings = {
  shareAttributes: boolean;
  shareJobHistory: boolean;
  shareDeploymentHistory: boolean;
};

/** Task/form inputs may use null for unset GraphQL InputMaybe fields */
export type AssetSharingSettingsInput = {
  shareAttributes?: boolean | null;
  shareJobHistory?: boolean | null;
  shareDeploymentHistory?: boolean | null;
};

/** System defaults used before tenant settings load, and for missing fields */
export const SYSTEM_ASSET_SHARING_DEFAULTS: AssetSharingSettings = {
  shareAttributes: true,
  shareJobHistory: true,
  shareDeploymentHistory: false,
};

export function resolveAssetSharingDefaults(
  tenantDefaults?: Partial<AssetSharingSettings> | null
): AssetSharingSettings {
  if (!tenantDefaults) {
    return { ...SYSTEM_ASSET_SHARING_DEFAULTS };
  }
  return {
    shareAttributes:
      tenantDefaults.shareAttributes ??
      SYSTEM_ASSET_SHARING_DEFAULTS.shareAttributes,
    shareJobHistory:
      tenantDefaults.shareJobHistory ??
      SYSTEM_ASSET_SHARING_DEFAULTS.shareJobHistory,
    shareDeploymentHistory:
      tenantDefaults.shareDeploymentHistory ??
      SYSTEM_ASSET_SHARING_DEFAULTS.shareDeploymentHistory,
  };
}

/**
 * Task-level sharing flags win when set; otherwise tenant/system defaults apply.
 */
export function mergeAssetSharingFields(
  taskAsset: AssetSharingSettingsInput | null | undefined,
  defaults: AssetSharingSettings
): AssetSharingSettings {
  return {
    shareAttributes: taskAsset?.shareAttributes ?? defaults.shareAttributes,
    shareJobHistory: taskAsset?.shareJobHistory ?? defaults.shareJobHistory,
    shareDeploymentHistory:
      taskAsset?.shareDeploymentHistory ?? defaults.shareDeploymentHistory,
  };
}
